import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { Download, X, Share, Smartphone } from "lucide-react";

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState("android"); // "ios" | "android"
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // 이미 PWA로 설치됐으면 표시 안 함
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (navigator.standalone) return;

    // 사용자가 닫기 했으면 7일간 표시 안 함
    const dismissedAt = localStorage.getItem("pause_install_dismissed_at");
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // iOS 감지
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    if (isiOS) {
      setPlatform("ios");
      setShowBanner(true);
      return;
    }

    // Android/Chrome — beforeinstallprompt 이벤트 감지
    setPlatform("android");
    setShowBanner(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    localStorage.setItem("pause_install_dismissed_at", String(Date.now()));
  };

  if (!showBanner || dismissed) return null;

  return (
    <div style={{
      padding: "14px 18px", marginBottom: 16, borderRadius: 12,
      background: T.card, border: `1px solid ${T.accent}25`,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
        <div style={{ padding: 8, background: T.accent + "15", borderRadius: 10, flexShrink: 0 }}>
          <Smartphone size={18} color={T.accent} strokeWidth={2} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Pause 앱 설치하기
          </div>
          {platform === "ios" ? (
            <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                Safari 하단 <Share size={12} color={T.accent} style={{ verticalAlign: "middle" }} /> 공유 버튼 → "홈 화면에 추가"를 눌러주세요
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
              {deferredPrompt
                ? "아래 설치 버튼을 눌러 홈화면에 추가할 수 있습니다"
                : <>Chrome 메뉴 <span style={{ fontWeight: 700 }}>⋮</span> → "홈 화면에 추가"를 눌러주세요</>
              }
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {platform === "android" && deferredPrompt && (
          <button onClick={handleInstall} style={{
            background: T.accent, color: "#fff", border: "none",
            borderRadius: 8, padding: "8px 16px", fontSize: 12,
            fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>
            설치
          </button>
        )}
        <button onClick={handleDismiss} style={{
          background: "none", border: "none", color: T.textMuted,
          cursor: "pointer", padding: 4,
        }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
